import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSiweAuth } from '../hooks/useSiweAuth';

/**
 * Wallet Context - Global wallet state management
 */
const WalletContext = createContext();

/**
 * Wallet Context Provider
 */
export const WalletProvider = ({ children }) => {
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize wallet state from localStorage on mount
  useEffect(() => {
    const storedAddress = localStorage.getItem('wallet_address');
    if (storedAddress) {
      setAddress(storedAddress);
    }
    setIsInitialized(true);
  }, []);

  // Check if MetaMask is installed
  const isMetaMaskInstalled = useCallback(() => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  }, []);

  // Format address for display
  const formatAddress = useCallback((addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }, []);

  // SIWE authentication hook
  const { handleSignIn, isSigningMessage } = useSiweAuth({
    statement: 'Sign in to XMAQUINA DAO to access your account',
    onSuccess: async ({ address: userAddress }) => {
      console.log('[WalletContext] SIWE authentication successful:', userAddress);
      
      // Set address
      setAddress(userAddress);
      
      // Try to read chain ID (fallbacks if window.ethereum absent)
      try {
        let chainIdHex = null;
        if (typeof window !== 'undefined' && window.ethereum) {
          chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
        }
        if (chainIdHex) setChainId(chainIdHex);
      } catch (err) {
        console.error('[WalletContext] Failed to get chain ID:', err);
      }
      
      setIsConnecting(false);
    },
    onError: (err) => {
      console.error('[WalletContext] SIWE authentication failed:', err);
      
      if (err.code === 4001) {
        setError('Signature rejected. Please approve the signature request in MetaMask.');
      } else if (err.message?.includes('personal_sign')) {
        setError('Failed to sign message. Please try again.');
      } else if (err.status === 400) {
        setError('Invalid signature. Please try connecting again.');
      } else {
        setError(err.message || 'Failed to connect wallet');
      }
      
      setIsConnecting(false);
    },
  });

  // Connect wallet using SIWE via MetaMask SDK
  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      await handleSignIn();
    } catch (err) {
      // Error already handled in onError callback
      console.error('[WalletContext] Connect failed:', err);
    }
  }, [handleSignIn]);

  // Disconnect wallet - local cleanup only (no backend in the read-only build)
  const disconnect = useCallback(async () => {
    setAddress(null);
    setChainId(null);
    setError(null);
    localStorage.removeItem('wallet_address');
    localStorage.removeItem('auth_token');
  }, []);
  
  // Update localStorage when address changes
  useEffect(() => {
    if (address) {
      localStorage.setItem('wallet_address', address);
    } else if (isInitialized) {
      localStorage.removeItem('wallet_address');
    }
  }, [address, isInitialized]);

  // Listen for account and chain changes (prefer SDK provider if available)
  useEffect(() => {
    let provider = null;
    try {
      // Prefer injected provider if present
      if (typeof window !== 'undefined' && window.ethereum) {
        provider = window.ethereum;
      }
    } catch (_) {}

    if (!provider || !address) return;

    const handleAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        disconnect();
      } else {
        setAddress(accounts[0]);
      }
    };

    const handleChainChanged = (newChainId) => {
      setChainId(newChainId);
    };

    provider.on && provider.on('accountsChanged', handleAccountsChanged);
    provider.on && provider.on('chainChanged', handleChainChanged);

    return () => {
      if (provider && provider.removeListener) {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [address, disconnect]);

  const isConnected = !!address;
  const formattedAddress = formatAddress(address);

  const value = {
    address,
    formattedAddress,
    chainId,
    isConnected,
    isConnecting: isConnecting || isSigningMessage,
    isInitialized,
    error,
    connect,
    disconnect,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

/**
 * Hook to use wallet context
 */
export const useGlobalWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useGlobalWallet must be used within WalletProvider');
  }
  return context;
};

export default WalletContext;
